import React, {useEffect, useState} from "react";
import Icon from "../../../components/AppIcon";
import * as LucideIcons from "lucide-react";

// Benefit structure types
type Stat = {
    label: string;
    value: number;
    suffix: string;
};

type Benefit = {
    id: string;
    icon: string;
    title: string;
    subtitle: string;
    description: string;
    stats: Stat[];
    features: string[];
    color: "primary" | "success" | "accent" | "secondary"; // Restrict color names
};

type AnimatedStats = Record<string, number | undefined>;

const benefits: Benefit[] = [
    {
        id: "cashless",
        icon: "CreditCard",
        title: "Cashless Operations",
        subtitle: "Eliminate Cash Handling Risks",
        description:
            "Transform to 100% digital transactions with mobile money and bank card integration",
        stats: [
            {label: "Revenue Leakage Reduction", value: 95, suffix: "%"},
            {label: "Transaction Security", value: 100, suffix: "%"},
            {label: "Processing Speed", value: 300, suffix: "% faster"},
        ],
        features: [
            "MTN Mobile Money, Vodafone Cash, AirtelTigo integration",
            "Real-time payment confirmation and receipts",
            "Automatic reconciliation with assembly accounts",
            "Fraud detection and prevention systems",
            "Multi-currency support for international payments",
        ],
        color: "primary",
    },
    {
        id: "transparent",
        icon: "Eye",
        title: "Transparent Accountability",
        subtitle: "Complete Audit Trail",
        description:
            "Every transaction tracked with immutable records and real-time reporting",
        stats: [
            {label: "Audit Compliance", value: 100, suffix: "%"},
            {label: "Real-time Visibility", value: 24, suffix: "/7"},
            {label: "Report Generation", value: 5, suffix: " seconds"},
        ],
        features: [
            "Blockchain-secured transaction records",
            "Real-time dashboard for all stakeholders",
            "Automated compliance reporting for government",
            "Citizen access to payment history and receipts",
            "Advanced analytics and forecasting tools",
        ],
        color: "success",
    },
    {
        id: "efficient",
        icon: "Zap",
        title: "Operational Efficiency",
        subtitle: "Streamlined Processes",
        description:
            "Reduce collection time by 85% with automated workflows and offline capabilities",
        stats: [
            {label: "Time Reduction", value: 85, suffix: "%"},
            {label: "Staff Productivity", value: 200, suffix: "% increase"},
            {label: "Citizen Satisfaction", value: 94, suffix: "%"},
        ],
        features: [
            "Offline collection with automatic sync",
            "GPS tracking for field collectors",
            "Automated receipt generation and printing",
            "Performance analytics for revenue officers",
            "Integration with existing assembly systems",
        ],
        color: "accent",
    },
];

const BenefitsGrid: React.FC = () => {
    const [expandedBenefit, setExpandedBenefit] = useState<string | null>(null);
    const [animatedStats, setAnimatedStats] = useState<AnimatedStats>({});

    useEffect(() => {
        // Animate statistics when component mounts
        const timer = setTimeout(() => {
            const newStats: AnimatedStats = {};
            benefits.forEach((benefit) => {
                benefit.stats.forEach((stat, index) => {
                    newStats[`${benefit.id}-${index}`] = stat.value;
                });
            });
            setAnimatedStats(newStats);
        }, 500);

        return () => clearTimeout(timer);
    }, []);

    const toggleExpanded = (benefitId: string) => {
        setExpandedBenefit((prev) => (prev === benefitId ? null : benefitId));
    };

    return (
        <section className="py-20 bg-gradient-to-br from-muted/30 to-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                        Transform Your Assembly Operations
                    </h2>
                    <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                        Three core benefits that revolutionize how Ghana's local assemblies
                        collect revenue and serve citizens.
                    </p>
                </div>

                {/* Benefits Grid */}
                <div className="grid lg:grid-cols-3 gap-8">
                    {benefits.map((benefit) => (
                        <div
                            key={benefit.id}
                            className={`bg-white rounded-2xl shadow-lg border border-border overflow-hidden transition-all duration-500 ${
                                expandedBenefit === benefit.id
                                    ? "lg:col-span-2 lg:row-span-2"
                                    : ""
                            }`}
                        >
                            {/* Benefit Header */}
                            <div
                                className={`p-8 bg-gradient-to-br from-${benefit.color}/5 to-${benefit.color}/10`}
                            >
                                <div className="flex items-start justify-between mb-6">
                                    <div
                                        className={`w-16 h-16 bg-${benefit.color}/10 rounded-2xl flex items-center justify-center`}
                                    >
                                        <Icon
                                            name={benefit.icon as keyof typeof LucideIcons}
                                            size={32}
                                            color={`var(--color-${benefit.color})`}
                                        />
                                    </div>
                                    <button
                                        onClick={() => toggleExpanded(benefit.id)}
                                        className={`p-2 rounded-lg bg-${benefit.color}/10 hover:bg-${benefit.color}/20 transition-colors`}
                                    >
                                        <Icon
                                            name={
                                                expandedBenefit === benefit.id
                                                    ? "ChevronUp"
                                                    : "ChevronDown"
                                            }
                                            size={20}
                                            color={`var(--color-${benefit.color})`}
                                        />
                                    </button>
                                </div>

                                <h3 className="text-2xl font-bold text-foreground mb-2">
                                    {benefit.title}
                                </h3>
                                <p className={`text-${benefit.color} font-medium mb-4`}>
                                    {benefit.subtitle}
                                </p>
                                <p className="text-muted-foreground">{benefit.description}</p>
                            </div>

                            {/* Statistics */}
                            <div className="p-6 border-b border-border">
                                <div className="grid grid-cols-1 gap-4">
                                    {benefit.stats.map((stat, statIndex) => (
                                        <div key={statIndex} className="text-center">
                                            <div
                                                className={`text-2xl font-bold text-${benefit.color} mb-1`}
                                            >
                                                {animatedStats[`${benefit.id}-${statIndex}`] || 0}
                                                {stat.suffix}
                                            </div>
                                            <p className="text-sm text-muted-foreground">
                                                {stat.label}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Expanded Features */}
                            {expandedBenefit === benefit.id && (
                                <div className="p-6 animate-fade-in">
                                    <h4 className="font-semibold text-foreground mb-4">
                                        Key Features
                                    </h4>
                                    <div className="space-y-3">
                                        {benefit.features.map((feature, featureIndex) => (
                                            <div
                                                key={featureIndex}
                                                className="flex items-start space-x-3"
                                            >
                                                <Icon
                                                    name="Check"
                                                    size={16}
                                                    color={`var(--color-${benefit.color})`}
                                                    className="mt-1 flex-shrink-0"
                                                />
                                                <span className="text-muted-foreground text-sm">
                          {feature}
                        </span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Implementation Timeline */}
                                    <div
                                        className={`mt-6 p-4 bg-${benefit.color}/5 rounded-lg`}
                                    >
                                        <h5 className="font-medium text-foreground mb-3">
                                            Implementation Timeline
                                        </h5>
                                        <div className="space-y-2">
                                            {[
                                                "Week 1-2: System setup and integration",
                                                "Week 3-4: Staff training and testing",
                                                "Week 5-6: Pilot launch and optimization",
                                            ].map((phase, i) => (
                                                <div
                                                    key={i}
                                                    className="flex items-center space-x-3"
                                                >
                                                    <div
                                                        className={`w-2 h-2 bg-${benefit.color} rounded-full`}
                                                    />
                                                    <span className="text-sm text-muted-foreground">
                            {phase}
                          </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Quick Action */}
                            {expandedBenefit !== benefit.id && (
                                <div className="p-6">
                                    <button
                                        onClick={() => toggleExpanded(benefit.id)}
                                        className={`w-full py-3 px-4 bg-${benefit.color}/10 text-${benefit.color} rounded-lg font-medium hover:bg-${benefit.color}/20 transition-colors`}
                                    >
                                        Learn More About {benefit.title}
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Success Metrics Summary */}
                <div className="mt-16 bg-white rounded-2xl p-8 shadow-lg">
                    <h3 className="text-2xl font-bold text-center text-foreground mb-8">
                        Proven Results from Pilot Assemblies
                    </h3>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        <div className="text-center">
                            <div className="text-3xl font-bold text-primary mb-2">₵2.4M</div>
                            <p className="text-sm text-muted-foreground">
                                Additional Revenue Captured
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="text-3xl font-bold text-success mb-2">98.7%</div>
                            <p className="text-sm text-muted-foreground">
                                Transaction Success Rate
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="text-3xl font-bold text-accent mb-2">15 Min</div>
                            <p className="text-sm text-muted-foreground">
                                Average Collection Time
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="text-3xl font-bold text-secondary mb-2">24/7</div>
                            <p className="text-sm text-muted-foreground">
                                Citizen Service Availability
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default BenefitsGrid;
